import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { FC, useEffect } from 'react'
import { useDocPageRevisionsLazyQuery } from '../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../constants'
import { RichTextRenderer } from './editor/RichTextRenderer'

interface RevisionHistoryDialogProps {
  open: boolean
  pageId?: string
  onClose: () => void
}

// Every publish from the editor records the user, so a revision with no editor was written by a
// script (e.g. the API's ReportsDocumentationSeeder) and is attributed to the system.
const revisionAuthor = (editor?: { firstName?: string | null; lastName?: string | null } | null) =>
  editor
    ? `Published by ${`${editor.firstName || ''} ${editor.lastName || ''}`.trim() || 'Unknown'}`
    : 'Updated by System'

export const RevisionHistoryDialog: FC<RevisionHistoryDialogProps> = ({ open, pageId, onClose }) => {
  const [fetchRevisions, { data, loading }] = useDocPageRevisionsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
  })

  useEffect(() => {
    if (open && pageId) fetchRevisions({ variables: { pageId } })
  }, [open, pageId, fetchRevisions])

  const revisions = (data?.docPageRevisions?.revisions || []).filter(
    (revision): revision is NonNullable<typeof revision> => !!revision,
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Revision history</DialogTitle>
      <DialogContent dividers>
        {loading && <Typography color="text.secondary">Loading…</Typography>}
        {!loading && revisions.length === 0 && (
          <Typography color="text.secondary">No published revisions yet.</Typography>
        )}
        {revisions.map((revision, index) => (
          <Accordion key={revision.id} defaultExpanded={index === 0} disableGutters>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box>
                <Typography variant="subtitle2">
                  {dayjs(revision.createdAt).format('MMM D, YYYY h:mm A')}
                  {index === 0 && ' (current)'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {revisionAuthor(revision.editor)}
                  {revision.changeSummary ? ` — ${revision.changeSummary}` : ''}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Divider sx={{ mb: 2 }} />
              <RichTextRenderer content={revision.content} emptyText="This revision had no content." />
            </AccordionDetails>
          </Accordion>
        ))}
      </DialogContent>
    </Dialog>
  )
}
